import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { DoubleCaretDown, DoubleCaretUp } from '../../../Icons/DoubleCaret';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { Field, useField } from 'react-final-form';
import { Checkbox, TextField } from '@material-ui/core';
import { AddCheckboxIcon } from '../../../../pages/CreateVehicle/icons/AddCheckboxIcon';
import { composeValidators, maxLength } from '../../../../utils/validators';
import { RedInfoIcon } from '../../../../pages/CreateVehicle/icons/RedInfoIcon';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid rgba(34, 90, 145, 1);
  background: rgba(34, 90, 145, 0.05);
  width: 100%;
  padding: 8px 16px 8px 10px;
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 16px;
  font-weight: 700;
  line-height: 18.75px;
  color: rgba(34, 90, 145, 1);
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  color: rgba(57, 148, 222, 1);
  cursor: pointer;
`;

const disabledCss = css`
  opacity: 0.7;
  pointer-events: none;
`;

const OptionsWrapper = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${props => props.disabled && disabledCss}
`;

const OptionsGridWrapper = styled.div<{ disabled?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: auto;
  grid-gap: 8px;
  ${props => props.disabled && disabledCss}
`;

const CheckboxWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const AddButtonWrapper = styled.div<{ noMargin?: boolean }>`
  margin-left: ${props => (props.noMargin ? '0' : 'auto')};
  cursor: pointer;
`;

const CheckboxLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 16.41px;
  color: rgba(102, 102, 102, 1);
`;

const FieldError = styled.div`
  position: absolute;
  display: flex;
  font-size: 12px;
  align-items: center;
  color: rgba(175, 41, 48, 1);
  gap: 8px;
  right: 0;
  bottom: -26px;
`;

const StyledTextField = styled(TextField)`
  flex-grow: 1;

  & * input {
    padding: 0;
  }
`;

const EditingFieldWrapper = styled.div<{ hasError: boolean }>`
  display: flex;
  border-bottom: ${props => (props.hasError ? '1px solid rgba(175, 41, 48, 1)' : '1px solid rgba(204, 204, 204, 1)')};
  gap: 8px;
  align-items: center;
  position: relative;
`;

type Props = {
  layout?: 'flex' | 'grid';
};

export const OptionalEquipment = ({ layout = 'flex', disabled = false, defaultOpen = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);

  const toggleExpanded = () => setIsExpanded(c => !c);

  const OptionsLayout = layout === 'grid' ? OptionsGridWrapper : OptionsWrapper;

  return (
    <Wrapper>
      <Header>
        <span>Optional Equipment</span>
        <IconWrapper onClick={toggleExpanded}>{isExpanded ? <DoubleCaretUp /> : <DoubleCaretDown />}</IconWrapper>
      </Header>
      {isExpanded && (
        <OptionsLayout disabled={disabled}>
          <FieldArray name={'options'}>
            {({ fields }) =>
              fields.map((fieldName, i) => (
                <CheckboxField isLast={i === fields.length! - 1} key={fieldName} fieldName={fieldName} />
              ))
            }
          </FieldArray>
        </OptionsLayout>
      )}
    </Wrapper>
  );
};

const CheckboxField = ({ fieldName, isLast }: { fieldName: string; isLast: boolean }) => {
  const { fields } = useFieldArray(`options`);

  const {
    input: { value: isEditing }
  } = useField(`${fieldName}.isEditing`);

  if (isEditing && isLast) {
    return <EditingEquipmentCheckbox fieldName={fieldName} />;
  }

  return (
    <CheckboxWrapper>
      <Field name={`${fieldName}.is_typical`}>
        {props => (
          <Checkbox
            color="primary"
            style={{ width: '24px', height: '24px' }}
            checked={props.input.value}
            onChange={e => {
              props.input.onChange(e.target.checked);
            }}
          />
        )}
      </Field>
      <Field name={`${fieldName}.option_name`}>{props => <CheckboxLabel>{props.input.value}</CheckboxLabel>}</Field>
      {isLast && (
        <AddButtonWrapper
          onClick={() =>
            fields.push({
              is_typical: true,
              isEditing: true,
              option_name: ''
            })
          }
        >
          <AddCheckboxIcon />
        </AddButtonWrapper>
      )}
    </CheckboxWrapper>
  );
};

const maxLength20 = maxLength(20, 'Max. 20 characters');

const EditingEquipmentCheckbox = ({ fieldName }: { fieldName: string }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { fields } = useFieldArray(`options`, { subscription: { value: true } });
  const { input: editingInput } = useField(`${fieldName}.isEditing`);
  const {
    input: { value: label, onChange: changeLabel },
    meta
  } = useField(`${fieldName}.option_name`);

  const submitCheckbox = async () => {
    inputRef.current?.blur();
    if (meta.error) {
      return;
    }
    if (!label) {
      return;
    }
    const parsedLabel = label.trim();
    editingInput.onChange(false);
    changeLabel(parsedLabel);
  };

  const duplicateValidator = (inputLabel: string) => {
    if (!inputLabel) return;
    const parsedLabel = inputLabel.trim();
    const isUnique = !fields.value.slice(0, -1).find(v => v.option_name === parsedLabel);
    if (!isUnique) {
      return 'Duplicate value';
    }
  };

  const hasError = !!meta.error && !!meta.touched;

  return (
    <EditingFieldWrapper hasError={hasError}>
      <Field validate={composeValidators(maxLength20, duplicateValidator)} name={`${fieldName}.option_name`}>
        {props => (
          <StyledTextField
            InputProps={{
              disableUnderline: true
            }}
            inputRef={inputRef}
            {...props.meta}
            {...props.input}
          />
        )}
      </Field>

      <AddButtonWrapper onClick={submitCheckbox}>
        <AddCheckboxIcon />
      </AddButtonWrapper>
      {hasError && (
        <FieldError>
          <span>{meta.error}</span>
          <RedInfoIcon />
        </FieldError>
      )}
    </EditingFieldWrapper>
  );
};
